import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Save, Copy, ExternalLink, ShoppingBag } from 'lucide-react';
interface MarketplacePricingModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: any;
}
export function MarketplacePricingModal({
  isOpen,
  onClose,
  product
}: MarketplacePricingModalProps) {
  if (!isOpen) return null;
  const marketplaces = [{
    id: 1,
    name: 'Site Próprio',
    price: product?.price,
    active: true,
    color: 'bg-navy-900'
  }, {
    id: 2,
    name: 'Amazon',
    price: product?.price * 1.05,
    active: true,
    color: 'bg-yellow-500'
  }, {
    id: 3,
    name: 'Mercado Livre',
    price: product?.price * 1.08,
    active: true,
    color: 'bg-yellow-400'
  }, {
    id: 4,
    name: 'Magazine Luiza',
    price: product?.price * 1.02,
    active: false,
    color: 'bg-blue-600'
  }, {
    id: 5,
    name: 'Americanas',
    price: product?.price * 1.02,
    active: false,
    color: 'bg-red-600'
  }];
  return <AnimatePresence data-id="element-231">
      <div className="fixed inset-0 z-50 flex justify-end" data-id="element-232">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm" data-id="element-233" />

        <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="relative bg-white w-full max-w-md h-full shadow-2xl flex flex-col z-50" data-id="element-234">
          <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 bg-white" data-id="element-235">
            <div data-id="element-236">
              <h2 className="text-lg font-bold text-navy-900" data-id="element-237">
                Preços por Marketplace
              </h2>
              <p className="text-sm text-slate-500 truncate max-w-[280px]" data-id="element-238">
                {product?.name}
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors" data-id="element-239">
              <X className="h-5 w-5 text-slate-500" data-id="element-240" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-241">
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6" data-id="element-242">
              <h3 className="text-sm font-semibold text-blue-900 mb-2" data-id="element-243">
                Estratégia de Precificação
              </h3>
              <p className="text-xs text-blue-700" data-id="element-244">
                Defina preços diferenciados para cada canal de venda para cobrir
                taxas de comissão e frete específicas.
              </p>
            </div>

            <div className="space-y-4" data-id="element-245">
              {marketplaces.map(mp => <div key={mp.id} className={`p-4 rounded-xl border transition-all ${mp.active ? 'border-slate-200 bg-white shadow-sm' : 'border-slate-100 bg-slate-50 opacity-70'}`} data-id="element-246">
                  <div className="flex items-center justify-between mb-3" data-id="element-247">
                    <div className="flex items-center space-x-3" data-id="element-248">
                      <div className={`h-8 w-8 rounded-lg ${mp.color} flex items-center justify-center text-white font-bold text-xs`} data-id="element-249">
                        {mp.name.charAt(0)}
                      </div>
                      <span className="font-medium text-navy-900" data-id="element-250">
                        {mp.name}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2" data-id="element-251">
                      <label className="relative inline-flex items-center cursor-pointer" data-id="element-252">
                        <input type="checkbox" className="sr-only peer" defaultChecked={mp.active} data-id="element-253" />
                        <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-navy-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500" data-id="element-254"></div>
                      </label>
                    </div>
                  </div>

                  <div className="flex items-center gap-3" data-id="element-255">
                    <div className="relative flex-1" data-id="element-256">
                      <span className="absolute left-3 top-2.5 text-slate-400 text-sm" data-id="element-257">
                        R$
                      </span>
                      <input type="number" defaultValue={mp.price?.toFixed(2)} disabled={!mp.active} className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-navy-900 disabled:bg-slate-100" data-id="element-258" />
                    </div>
                    <button className="p-2 text-slate-400 hover:text-navy-900 hover:bg-slate-100 rounded-lg transition-colors" title="Copiar preço base" data-id="element-259">
                      <Copy className="h-4 w-4" data-id="element-260" />
                    </button>
                    <button className="p-2 text-slate-400 hover:text-navy-900 hover:bg-slate-100 rounded-lg transition-colors" title="Ver anúncio" data-id="element-261">
                      <ExternalLink className="h-4 w-4" data-id="element-262" />
                    </button>
                  </div>
                </div>)}
            </div>
          </div>

          <div className="p-4 border-t border-slate-200 bg-slate-50" data-id="element-263">
            <div className="flex gap-2 mb-4" data-id="element-264">
              <select className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-navy-900" data-id="element-265">
                <option data-id="element-266">Ações em massa...</option>
                <option data-id="element-267">Aplicar +10% em todos</option>
                <option data-id="element-268">Copiar preço base para todos</option>
                <option data-id="element-269">Resetar alterações</option>
              </select>
              <button className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100" data-id="element-270">
                Aplicar
              </button>
            </div>
            <button onClick={onClose} className="w-full py-3 bg-navy-900 text-white rounded-xl text-sm font-bold hover:bg-navy-800 transition-colors shadow-lg shadow-navy-900/20 flex items-center justify-center" data-id="element-271">
              <Save className="h-4 w-4 mr-2" data-id="element-272" />
              Salvar Preços
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}